import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { encryptLoginData, encryptRegisterData } from '../utils/encryption';

interface MenuItem {
  key: string;
  icon: string;
  label: string;
  children?: MenuItem[];
}

interface User {
  id: number;
  username: string;
  email: string;
  avatar_url?: string;
  is_admin?: boolean;
  is_active?: boolean;
  expires_at?: string | null;
  is_wechat_auth_enabled?: boolean;
  api_token?: string;
  role?: string;
  custom_menu_permissions?: string[] | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  menuItems: MenuItem[];
  allowedPaths: Set<string>;
  login: (username: string, password: string, deviceFingerprint?: string, captchaId?: string, captchaAnswer?: string) => Promise<void>;
  register: (username: string, email: string, password: string, invite_code?: string, deviceFingerprint?: string, captchaInfo?: { captcha_id: string; captcha_answer: string }) => Promise<void>;
  logout: () => void;
  refreshAuthToken: () => Promise<boolean>;
  loading: boolean;
  updateUser: (user?: User | null) => Promise<void>;
  fetchMenuPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>(() => {
    try {
      const stored = localStorage.getItem('menuItems');
      if (stored) return JSON.parse(stored);
    } catch (e) { /* ignore */ }
    return [];
  });
  const [allowedPaths, setAllowedPaths] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('allowedPaths');
      if (stored) return new Set<string>(JSON.parse(stored));
    } catch (e) { /* ignore */ }
    return new Set();
  });

  const fetchMenuPermissions = async () => {
    try {
      const response = await authAPI.getMenuPermissions();
      if (response.data.status === 200) {
        if (response.data.menus) {
          setMenuItems(response.data.menus);
        }
        if (response.data.paths) {
          const paths = new Set<string>(response.data.paths);
          setAllowedPaths(paths);
        }
        localStorage.setItem('menuItems', JSON.stringify(response.data.menus || []));
        localStorage.setItem('allowedPaths', JSON.stringify(response.data.paths || []));
      }
    } catch (error) {
      console.error('获取菜单权限失败:', error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // 只在首次加载时执行，避免注册后重复验证
    if (isInitialized) return;
    
    const storedToken = localStorage.getItem('token');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
      try {
        setToken(storedToken);
        setRefreshToken(storedRefreshToken);
        setUser(JSON.parse(storedUser));

        // 验证token是否仍然有效
        authAPI.getProfile()
          .then(response => {
            setUser(response.data.user);
            setIsInitialized(true);
            fetchMenuPermissions();
          })
          .catch(async (error) => {
            console.warn('Token验证失败，尝试刷新:', error);
            // Token无效，尝试刷新
            if (storedRefreshToken) {
              const refreshSuccess = await refreshAuthToken();
              if (refreshSuccess) {
                fetchMenuPermissions();
              } else {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                localStorage.removeItem('menuItems');
                localStorage.removeItem('allowedPaths');
                setToken(null);
                setRefreshToken(null);
                setUser(null);
                setMenuItems([]);
                setAllowedPaths(new Set());
              }
            } else {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              localStorage.removeItem('menuItems');
              localStorage.removeItem('allowedPaths');
              setToken(null);
              setRefreshToken(null);
              setUser(null);
              setMenuItems([]);
              setAllowedPaths(new Set());
            }
            setIsInitialized(true);
            setLoading(false);
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.error('解析用户数据失败:', error);
        // 清除无效的本地存储
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('menuItems');
        localStorage.removeItem('allowedPaths');
        setToken(null);
        setRefreshToken(null);
        setUser(null);
        setMenuItems([]);
        setAllowedPaths(new Set());
        setLoading(false);
        setIsInitialized(true);
      }
    } else {
      setLoading(false);
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  const refreshAuthToken = async (): Promise<boolean> => {
    try {
      const currentRefreshToken = refreshToken || localStorage.getItem('refreshToken');
      if (!currentRefreshToken) {
        return false;
      }

      const response = await authAPI.refreshToken({ refreshToken: currentRefreshToken });
      const { token: newToken, refreshToken: newRefreshToken, user: newUser } = response.data;
      
      setToken(newToken);
      setRefreshToken(newRefreshToken);
      setUser(newUser);
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      // 确保状态正确
      setLoading(false);
      setIsInitialized(true);
      
      return true;
    } catch (error) {
      console.error('Token刷新失败:', error);
      return false;
    }
  };

  const login = async (username: string, password: string, deviceFingerprint?: string, captchaId?: string, captchaAnswer?: string) => {
    try {
      // 使用 RSA 加密登录数据
      const encryptedData = await encryptLoginData(username, password);
      const loginPayload: Record<string, any> = {
        ...encryptedData,
        device_fingerprint: deviceFingerprint || '',
      };
      if (captchaId && captchaAnswer) {
        loginPayload.captcha_id = captchaId;
        loginPayload.captcha_answer = captchaAnswer;
      }

      const response = await authAPI.login(loginPayload as { username: string; password: string });
      
      // 检查账号是否被禁用且未到期
      if (response.data.accountDisabled) {
        const error = new Error('账号已被禁用，请联系管理员') as any;
        error.accountDisabled = true;
        error.isExpired = false;
        throw error;
      }
      
      const { token: newToken, refreshToken: newRefreshToken, user: newUser, isExpired } = response.data;
      
      setToken(newToken);
      setRefreshToken(newRefreshToken);
      setUser(newUser);
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      // 确保loading状态正确，避免ProtectedRoute阻止访问
      setLoading(false);
      setIsInitialized(true);

      fetchMenuPermissions();
      
      // 如果账户已到期，返回标记以便前端处理
      if (isExpired) {
        const error = new Error('账户已到期') as any;
        error.isExpired = true;
        throw error;
      }
    } catch (error: any) {
      // 如果是账户已到期或账户被禁用的特殊错误，直接抛出
      if (error.accountDisabled !== undefined || error.isExpired !== undefined) {
        throw error;
      }
      
      // 检查响应数据中是否包含 accountDisabled 或 isExpired 标记
      if (error.response?.data?.accountDisabled !== undefined) {
        const accountError = new Error(error.response.data.error || '账号已被禁用，请联系管理员') as any;
        accountError.accountDisabled = error.response.data.accountDisabled;
        accountError.isExpired = error.response.data.isExpired || false;
        throw accountError;
      }
      
      if (error.response?.data?.isExpired !== undefined) {
        const expiredError = new Error(error.response.data.error || '账户已到期') as any;
        expiredError.isExpired = error.response.data.isExpired;
        throw expiredError;
      }
      
      // 登录错误处理
      // 根据不同的错误状态码返回不同的错误信息
      if (error.response?.status === 401) {
        throw new Error('用户名或密码错误');
      } else if (error.response?.status === 404) {
        throw new Error('用户不存在');
      } else if (error.response?.status === 400) {
        throw new Error(error.response?.data?.error || '请求参数错误');
      } else if (error.response?.status >= 500) {
        throw new Error('服务器错误，请稍后重试');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error('网络连接失败，请检查网络设置');
      } else {
        throw new Error(error.response?.data?.error || error.message || '登录失败，请重试');
      }
    }
  };

  const register = async (username: string, email: string, password: string, invite_code?: string, deviceFingerprint?: string, captchaInfo?: { captcha_id: string; captcha_answer: string }) => {
    try {
      // 使用 RSA 加密注册数据
      const encryptedData = await encryptRegisterData(username, email, password, invite_code);
      const registerPayload: Record<string, any> = {
        ...encryptedData,
        device_fingerprint: deviceFingerprint || '',
      };

      // 添加验证码信息
      if (captchaInfo) {
        registerPayload.captcha_id = captchaInfo.captcha_id;
        registerPayload.captcha_answer = captchaInfo.captcha_answer;
      }

      const response = await authAPI.register(registerPayload as { username: string; email: string; password: string; invite_code?: string });
      
      // 检查响应数据是否包含必要的字段
      if (!response.data || !response.data.token || !response.data.refreshToken || !response.data.user) {
        throw new Error('注册响应数据不完整，请重试');
      }
      
      const { token: newToken, refreshToken: newRefreshToken, user: newUser } = response.data;
      
      // 确保token和user都存在
      if (!newToken || !newRefreshToken || !newUser) {
        throw new Error('注册成功但无法获取登录信息，请重新登录');
      }
      
      // 设置token和用户信息
      setToken(newToken);
      setRefreshToken(newRefreshToken);
      setUser(newUser);
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      // 设置loading为false，确保ProtectedRoute不会因为loading状态而阻止访问
      // 同时标记为已初始化，避免useEffect重复验证
      setLoading(false);
      setIsInitialized(true);

      fetchMenuPermissions();
    } catch (error: any) {
      // 注册错误处理
      
      // 根据不同的错误状态码返回不同的错误信息
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error;
        // 直接使用服务器返回的错误信息，或者提供默认信息
        throw new Error(errorMessage || '注册信息有误，请检查输入');
      } else if (error.response?.status >= 500) {
        throw new Error('服务器错误，请稍后重试');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error('网络连接失败，请检查网络设置');
      } else {
        throw new Error(error.response?.data?.error || error.message || '注册失败，请重试');
      }
    }
  };

  const logout = () => {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setMenuItems([]);
    setAllowedPaths(new Set());
    setLoading(false);
    setIsInitialized(false);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('menuItems');
    localStorage.removeItem('allowedPaths');
  };

  const updateUser = async (updatedUser?: User | null) => {
    if (updatedUser !== undefined) {
      // 如果提供了用户对象，直接更新
      setUser(updatedUser);
      if (updatedUser) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else {
        localStorage.removeItem('user');
      }
    } else {
      // 如果没有提供用户对象，从API获取最新用户信息
      try {
        const response = await authAPI.getProfile();
        const newUser = response.data.user;
        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
      } catch (error) {
        console.error('更新用户信息失败:', error);
      }
    }
  };

  const value = {
    user,
    token,
    refreshToken,
    menuItems,
    allowedPaths,
    login,
    register,
    logout,
    refreshAuthToken,
    loading,
    updateUser,
    fetchMenuPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

